import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const formatCurrency = (amount, currency = 'EUR') => {
  if (amount == null || amount === '') return '—';
  try {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).format(Number(amount));
  } catch {
    return `${Number(amount).toFixed(2)} ${currency}`;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(dateStr));
  } catch {
    return dateStr;
  }
};

const STATUS_LABELS = {
  paid:            'Payé',
  pending:         'En attente',
  overdue:         'En retard',
  partial:         'Partiel',
  partially_paid:  'Partiellement payé',
  cancelled:       'Annulé',
  draft:           'Brouillon',
  in_collection:   'En recouvrement',
  waived:          'Exonéré',
};

/**
 * Factures liées à une conception (case_id) via case_phase_invoices.
 * Expose raw numeric fields + currencyCode so the PDF generator can use them.
 */
export function useCaseInvoices(caseId) {
  const [state, setState] = useState({
    invoices: [],
    loading: true,
    error: null,
  });

  const fetchInvoices = useCallback(async () => {
    if (!caseId) {
      setState({ invoices: [], loading: false, error: null });
      return;
    }

    setState((s) => ({ ...s, loading: true, error: null }));

    try {
      // ── 1. Get the case to resolve the dentist's currency ──
      const { data: caseRow, error: caseErr } = await supabase
        .from('cases')
        .select(`
          id,
          case_number,
          patient:patients ( id, name, patient_code ),
          dentist:user_profiles!cases_user_profile_id_fkey (
            id,
            full_name,
            email,
            country:countries (
              id,
              currency_code
            )
          )
        `)
        .eq('id', caseId)
        .maybeSingle();

      if (caseErr) throw caseErr;

      const currencyCode =
        caseRow?.dentist?.country?.currency_code || 'EUR';
      const caseNumber   = caseRow?.case_number  || '—';
      const patientName  = caseRow?.patient?.name || '—';
      const dentistName  = caseRow?.dentist?.full_name || '—';
      const dentistEmail = caseRow?.dentist?.email || '—';

      // ── 2. Fetch phase invoices ──
      const { data: rows, error: phaseErr } = await supabase
        .from('case_phase_invoices')
        .select(`
          id,
          phase_number,
          phase_name,
          aligners_produced_at_billing,
          phase_price,
          status,
          billing_date,
          production_batch_id,
          billing_phase:package_billing_phases (
            aligner_range_from,
            aligner_range_to
          ),
          invoice:invoices (
            id,
            invoice_number,
            status,
            issue_date,
            due_date,
            payment_date,
            total_amount,
            subtotal,
            amount_paid,
            balance_due,
            discount_amount,
            tax_amount,
            tax_percentage,
            payment_method,
            notes
          ),
          batch:production_batches!case_phase_invoices_production_batch_id_fkey (
            batch_number
          )
        `)
        .eq('case_id', caseId)
        .not('invoice', 'is', null)
        .order('phase_number');

      if (phaseErr) throw phaseErr;

      const now = new Date().toISOString().split('T')[0];

      const invoices = (rows ?? []).map((row) => {
        const inv = row.invoice;
        if (!inv) return null;

        // ── raw numeric values (used by PDF) ──
        const rawTotal       = Number(inv.total_amount  ?? 0);
        const rawSubtotal    = Number(inv.subtotal      ?? inv.total_amount ?? 0);
        const rawAmountPaid  = Number(inv.amount_paid   ?? 0);
        const rawBalanceDue  = Number(inv.balance_due   ?? 0);
        const rawDiscount    = Number(inv.discount_amount ?? 0);
        const rawTax         = Number(inv.tax_amount    ?? 0);

        // ── derived status ──
        let status = inv.status;
        if (
          status !== 'paid' &&
          status !== 'cancelled' &&
          status !== 'waived' &&
          inv.due_date &&
          inv.due_date < now
        ) {
          status = 'overdue';
        }

        return {
          // identifiers
          id:            inv.id,
          number:        inv.invoice_number,
          status,

          // phase info
          phaseNumber:       row.phase_number,
          phaseName:         row.phase_name,
          alignersCount:     row.aligners_produced_at_billing,
          alignerRangeFrom:  row.billing_phase?.aligner_range_from,
          alignerRangeTo:    row.billing_phase?.aligner_range_to,

          // lot
          lotNumber: row.batch?.batch_number || '—',

          // case / patient / dentist (for PDF header)
          caseNumber,
          patientName,
          dentistName,
          dentistEmail,

          // currency (for PDF fmt())
          currencyCode,

          // ── RAW numbers for PDF ──
          rawTotal,
          rawSubtotal,
          rawAmountPaid,
          rawBalanceDue,
          rawDiscount,
          rawTax,
          taxPercentage: inv.tax_percentage ?? 0,

          // raw dates for PDF fmtDate()
          rawIssueDate:   inv.issue_date,
          rawDueDate:     inv.due_date,
          rawPaymentDate: inv.payment_date,

          // ── formatted strings for UI display ──
          total:         formatCurrency(rawTotal,      currencyCode),
          subtotal:      formatCurrency(rawSubtotal,   currencyCode),
          amountPaid:    formatCurrency(rawAmountPaid, currencyCode),
          balanceDue:    formatCurrency(rawBalanceDue, currencyCode),
          discount:      rawDiscount > 0 ? formatCurrency(rawDiscount, currencyCode) : null,

          issueDate:     formatDate(inv.issue_date),
          dueDate:       formatDate(inv.due_date),
          paymentDate:   formatDate(inv.payment_date),
          paymentMethod: inv.payment_method || '—',
          notes:         inv.notes || null,
        };
      }).filter(Boolean);

      const invoiceIds = invoices.map((i) => i.id).filter(Boolean);
      if (invoiceIds.length > 0) {
        const { data: lineRows } = await supabase
          .from('invoice_line_items')
          .select('*')
          .in('invoice_id', invoiceIds)
          .order('sort_order', { ascending: true });

        const linesByInvoice = (lineRows || []).reduce((acc, row) => {
          const id = row.invoice_id;
          if (!acc[id]) acc[id] = [];
          acc[id].push({
            description: row.description,
            quantity: row.quantity,
            unitPrice: Number(row.unit_price),
            lineTotal: Number(row.line_total),
            lineType: row.line_type,
          });
          return acc;
        }, {});

        invoices.forEach((inv) => {
          inv.lineItems = linesByInvoice[inv.id] || [];
        });
      }

      setState({ invoices, loading: false, error: null });
    } catch (err) {
      console.error('[useCaseInvoices]', err);
      setState({ invoices: [], loading: false, error: err.message });
    }
  }, [caseId]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  return { ...state, refetch: fetchInvoices };
}